<template>
  <div v-if="show" class="jf-screen">

    <!-- Question card -->
    <div v-if="question" class="jf-question-card">
      <p class="jf-question">{{ question }}</p>
    </div>

    <!-- Progress -->
    <div class="jf-progress-bar">
      <div class="jf-progress-fill" :style="{ width: (answeredCount / Math.max(items.length, 1) * 100) + '%' }"></div>
      <span class="jf-progress-label">{{ answeredCount }} / {{ items.length }}</span>
    </div>

    <!-- Bravo overlay -->
    <transition name="jf-bravo">
      <div v-if="allDone" class="jf-bravo-overlay">
        <div class="jf-bravo-card">
          <div class="jf-bravo-emoji">🎉</div>
          <p class="jf-bravo-text">{{ translation.bravo }}</p>
        </div>
      </div>
    </transition>

    <!-- Items list -->
    <div class="jf-items-list">
      <div
        v-for="(item, i) in items"
        :key="i"
        :class="['jf-item-card', { 'jf-item-done': states[i] && states[i].answered }]"
      >
        <div class="jf-item-label">{{ item.label }}</div>
        <div class="jf-btn-row">
          <button
            :class="['jf-btn', 'jf-btn-vrai',
              { 'jf-btn-success': states[i] && states[i].answered && item.correct === true,
                'jf-btn-flash': states[i] && states[i].wrongVrai }]"
            :disabled="states[i] && states[i].answered"
            @click="checkAnswer(i, true)"
          >{{ translation.vrai }}</button>
          <button
            :class="['jf-btn', 'jf-btn-faux',
              { 'jf-btn-success': states[i] && states[i].answered && item.correct !== true,
                'jf-btn-flash': states[i] && states[i].wrongFaux }]"
            :disabled="states[i] && states[i].answered"
            @click="checkAnswer(i, false)"
          >{{ translation.faux }}</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  props: {
    pointData: { type: Object, default: null },
  },
  data() {
    return {
      correctAnswerSound: require("../../static/sounds/correctAnswer.mp3"),
      wrongAnswerSound: require("../../static/sounds/wrongAnswer.mp3"),
      states: [],
      allDone: false,
      translation: { vrai: 'Vrai', faux: 'Faux', bravo: 'Excellent !' },
    };
  },
  computed: {
    show() {
      return this.$parent.showJusteFaux === true;
    },
    question() {
      return this.pointData?.justeFauxQuestion || '';
    },
    items() {
      const raw = this.pointData?.justeFauxItems;
      if (!raw) return [];
      return Array.isArray(raw) ? raw : Object.values(raw);
    },
    answeredCount() {
      return this.states.filter(s => s && s.answered).length;
    },
  },
  watch: {
    items: {
      immediate: true,
      handler(val) {
        this.states = (val || []).map(() => ({ answered: false, wrongVrai: false, wrongFaux: false }));
      },
    },
  },
  mounted() {
    this.translate(this.$store.getters.lang);
  },
  methods: {
    setState(index, newState) {
      // Vue 3: splice triggers reactivity on arrays
      this.states.splice(index, 1, newState);
    },

    checkAnswer(index, answer) {
      if (!this.states[index] || this.states[index].answered) return;
      const item = this.items[index];
      const isCorrect = (answer === true) === (item.correct === true);

      if (isCorrect) {
        this.setState(index, { answered: true, wrongVrai: false, wrongFaux: false });
        new Audio(this.correctAnswerSound).play().catch(() => {});
        this.$parent.errorNumber++;
        this.$parent.showStarUp('1+');
        this.$parent.showCorrectAnswerIcon = true;
        localStorage.setItem('errorNumber', this.$parent.errorNumber);

        setTimeout(() => {
          this.$parent.showCorrectAnswerIcon = false;
          if (this.states.every(s => s && s.answered)) {
            this.allDone = true;
            setTimeout(() => { this.$emit('jf-done'); }, 2000);
          }
        }, 800);

      } else {
        const wrongVrai = answer === true;
        this.setState(index, { answered: false, wrongVrai, wrongFaux: !wrongVrai });
        new Audio(this.wrongAnswerSound).play().catch(() => {});
        this.$parent.errorNumber--;
        if (this.$parent.$refs.pointsCounting) {
          this.$parent.$refs.pointsCounting.classList.add('redColor');
        }
        this.$parent.showWongAnswerIcon = true;
        localStorage.setItem('errorNumber', this.$parent.errorNumber);

        setTimeout(() => {
          this.setState(index, { answered: false, wrongVrai: false, wrongFaux: false });
          if (this.$parent.$refs.pointsCounting) {
            this.$parent.$refs.pointsCounting.classList.remove('redColor');
          }
          this.$parent.showWongAnswerIcon = false;
        }, 1000);
      }
    },

    translate(lang) {
      const map = {
        fr: { vrai: 'Vrai', faux: 'Faux', bravo: 'Excellent ! Toutes les bonnes réponses !' },
        he: { vrai: 'נכון', faux: 'לא נכון', bravo: '!מצוין! כל התשובות נכונות' },
        en: { vrai: 'True', faux: 'False', bravo: 'Excellent! All correct!' },
        de: { vrai: 'Wahr', faux: 'Falsch', bravo: 'Ausgezeichnet! Alles richtig!' },
        ar: { vrai: 'صحيح', faux: 'خطأ', bravo: '!ممتاز! كل الإجابات صحيحة' },
      };
      this.translation = map[lang] || map.fr;
    },
  },
};
</script>

<style scoped>
/* ── Root ─────────────────────────────────────────────── */
.jf-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 12px 80px;
  min-height: 60vh;
  gap: 18px;
  width: 100%;
}

/* ── Question card ────────────────────────────────────── */
.jf-question-card {
  width: 100%;
  max-width: 560px;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 20px;
  padding: 28px 24px;
}

.jf-question {
  font-family: 'VarelaRound', sans-serif;
  font-size: 22px;
  font-weight: bold;
  color: white;
  text-align: center;
  margin: 0;
  line-height: 1.5;
}

/* ── Progress bar ─────────────────────────────────────── */
.jf-progress-bar {
  position: relative;
  width: 100%;
  max-width: 560px;
  height: 22px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 11px;
  overflow: hidden;
}

.jf-progress-fill {
  height: 100%;
  background: #4caf50;
  border-radius: 11px;
  transition: width 0.4s ease;
}

.jf-progress-label {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'VarelaRound', sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: white;
}

/* ── Items list ───────────────────────────────────────── */
.jf-items-list {
  width: 100%;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* ── Item card ────────────────────────────────────────── */
.jf-item-card {
  background: rgba(0, 0, 0, 0.42);
  border: 1.5px solid rgba(255, 255, 255, 0.2);
  border-radius: 18px;
  padding: 20px 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: border-color 0.3s, background 0.3s;
}

.jf-item-done {
  border-color: rgba(76, 175, 80, 0.75);
  background: rgba(76, 175, 80, 0.12);
}

.jf-item-label {
  font-family: 'VarelaRound', sans-serif;
  font-size: 22px;
  font-weight: bold;
  color: white;
  text-align: center;
  line-height: 1.4;
}

/* ── Button row ───────────────────────────────────────── */
.jf-btn-row {
  display: flex;
  gap: 14px;
}

/* ── Buttons ──────────────────────────────────────────── */
.jf-btn {
  flex: 1;
  font-family: 'VarelaRound', sans-serif;
  font-size: 20px;
  font-weight: bold;
  border: 3px solid transparent;
  border-radius: 50px;
  min-height: 58px;
  cursor: pointer;
  transition: transform 80ms, box-shadow 80ms, background 0.2s, border-color 0.2s;
  color: white;
}

.jf-btn:active:not(:disabled) {
  transform: translateY(3px);
}

.jf-btn:disabled {
  cursor: default;
}

.jf-btn-vrai {
  background: #1976d2;
  box-shadow: 0 5px 0 #0d47a1, 0 8px 12px rgba(0, 0, 0, 0.35);
}

.jf-btn-vrai:active:not(:disabled) {
  box-shadow: 0 2px 0 #0d47a1, 0 2px 6px rgba(0, 0, 0, 0.2);
}

.jf-btn-faux {
  background: #d32f2f;
  box-shadow: 0 5px 0 #7f0000, 0 8px 12px rgba(0, 0, 0, 0.35);
}

.jf-btn-faux:active:not(:disabled) {
  box-shadow: 0 2px 0 #7f0000, 0 2px 6px rgba(0, 0, 0, 0.2);
}

/* Correct answer — green border, lock in place */
.jf-btn-success {
  background: #388e3c !important;
  border-color: #a5d6a7 !important;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.45), 0 5px 0 #1b5e20 !important;
  opacity: 1 !important;
}

/* Wrong answer flash + shake */
.jf-btn-flash {
  animation: jf-shake 0.35s ease;
}

@keyframes jf-shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-7px); }
  50% { transform: translateX(7px); }
  75% { transform: translateX(-4px); }
}

/* ── Bravo overlay ────────────────────────────────────── */
.jf-bravo-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.jf-bravo-card {
  background: linear-gradient(135deg, #1b5e20, #2e7d32);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 28px;
  padding: 40px 48px;
  text-align: center;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5);
}

.jf-bravo-emoji {
  font-size: 60px;
  line-height: 1;
  margin-bottom: 16px;
}

.jf-bravo-text {
  font-family: 'VarelaRound', sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #fff;
  margin: 0;
}

.jf-bravo-enter-active { animation: jf-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
@keyframes jf-pop {
  from { opacity: 0; transform: scale(0.5); }
  to   { opacity: 1; transform: scale(1); }
}
</style>
